import { useEffect, useState } from 'react';
import {
  AlertCircle,
  BarChart3,
  FileSignature,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { ModalPortal } from '../components/ModalPortal';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Page Contrats (Lot D — US client, 2e temps).
 *
 * Gestion des contrats de maintenance par prestataire + comparaison du réalisé
 * (interventions clôturées) au contractuel (volume/montant proratés) sur une
 * période donnée.
 */

interface ContratRow {
  id: string;
  reference: string;
  libelle: string;
  type_contrat: string;
  statut: string;
  date_debut: string;
  date_fin: string;
  montant_annuel: number | null;
  nb_interventions_prevues: number | null;
  delai_intervention_h: number | null;
  services_couverts: string[];
  equipements_couverts: string[];
  notes: string | null;
  prestataire: { id: string; nom: string } | null;
}

interface PrestataireOption {
  id: string;
  nom: string;
}

interface EquipementOption {
  id: string;
  code_equipement: string;
  designation: string;
}

const TYPE_LABELS: Record<string, string> = {
  maintenance_preventive: 'Maintenance préventive',
  maintenance_corrective: 'Maintenance corrective',
  full_service: 'Full service',
  garantie: 'Garantie',
};

const STATUT_BADGE: Record<string, string> = {
  brouillon: 'bg-gray-100 text-gray-700',
  actif: 'bg-green-100 text-green-700',
  expire: 'bg-orange-100 text-orange-700',
  resilie: 'bg-red-100 text-red-700',
};

export function ContratsPage() {
  const { can } = usePermissions();
  const canEdit = can('interventions', 'update');
  const canDelete = can('interventions', 'delete');

  const [items, setItems] = useState<ContratRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContratRow | null>(null);
  const [comparing, setComparing] = useState<ContratRow | null>(null);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: ContratRow[] }>('/contrats?page_size=200');
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handleDelete(c: ContratRow) {
    if (!confirm(`Supprimer le contrat "${c.reference}" ?`)) return;
    try {
      await api.delete(`/contrats/${c.id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileSignature className="w-7 h-7 mr-2 text-primary-600" />
            Contrats de maintenance
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Engagements contractuels par prestataire + comparaison réalisé / contractuel.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Nouveau contrat
          </button>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Référence</Th>
                <Th>Libellé</Th>
                <Th>Prestataire</Th>
                <Th>Type</Th>
                <Th>Statut</Th>
                <Th>Période</Th>
                <Th>Montant annuel</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun contrat enregistré.
                  </td>
                </tr>
              ) : (
                items.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                      {c.reference}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{c.libelle}</td>
                    <td className="px-3 py-2 text-gray-700">{c.prestataire?.nom ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {TYPE_LABELS[c.type_contrat] ?? c.type_contrat}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${
                          STATUT_BADGE[c.statut] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {c.statut}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-xs">
                      {fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {c.montant_annuel != null
                        ? `${c.montant_annuel.toLocaleString('fr-FR')} €`
                        : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setComparing(c)}
                          className="p-1.5 rounded text-primary-600 hover:bg-primary-50"
                          title="Comparaison réalisé / contractuel"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => {
                              setEditing(c);
                              setFormOpen(true);
                            }}
                            className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(c)}
                            className="p-1.5 rounded text-red-600 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <ContratFormModal
          contrat={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            refresh();
          }}
        />
      )}

      {comparing && <ComparaisonModal contrat={comparing} onClose={() => setComparing(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ContratFormModal({
  contrat,
  onClose,
  onSaved,
}: {
  contrat: ContratRow | null;
  onClose(): void;
  onSaved(): void;
}) {
  const [prestataires, setPrestataires] = useState<PrestataireOption[]>([]);
  const [equipOptions, setEquipOptions] = useState<EquipementOption[]>([]);
  const [equipements, setEquipements] = useState<string[]>(contrat?.equipements_couverts ?? []);
  const [equipSearch, setEquipSearch] = useState('');
  const [form, setForm] = useState({
    prestataire_id: contrat?.prestataire?.id ?? '',
    reference: contrat?.reference ?? '',
    libelle: contrat?.libelle ?? '',
    type_contrat: contrat?.type_contrat ?? 'maintenance_preventive',
    statut: contrat?.statut ?? 'actif',
    date_debut: contrat?.date_debut?.slice(0, 10) ?? '',
    date_fin: contrat?.date_fin?.slice(0, 10) ?? '',
    montant_annuel: contrat?.montant_annuel != null ? String(contrat.montant_annuel) : '',
    nb_interventions_prevues:
      contrat?.nb_interventions_prevues != null ? String(contrat.nb_interventions_prevues) : '',
    delai_intervention_h:
      contrat?.delai_intervention_h != null ? String(contrat.delai_intervention_h) : '',
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: PrestataireOption[] }>('/prestataires?is_active=true&page_size=200')
      .then((res) => setPrestataires(res.data.data))
      .catch(() => setPrestataires([]));
  }, []);

  // Charge tous les équipements (paginé, le endpoint plafonne à 200) pour
  // permettre la sélection des équipements couverts par le contrat.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all: EquipementOption[] = [];
      for (let page = 1; page <= 50; page++) {
        const res = await api.get<{ data: EquipementOption[] }>(
          `/equipements?page_size=200&page=${page}&sort_by=code_equipement&sort_order=asc`,
        );
        all.push(...res.data.data);
        if (res.data.data.length < 200) break;
      }
      if (!cancelled) setEquipOptions(all);
    })().catch(() => {
      if (!cancelled) setEquipOptions([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleEquip(id: string) {
    setEquipements((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  }

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    const payload = {
      prestataire_id: form.prestataire_id,
      reference: form.reference,
      libelle: form.libelle,
      type_contrat: form.type_contrat,
      statut: form.statut,
      date_debut: form.date_debut,
      date_fin: form.date_fin,
      montant_annuel: form.montant_annuel ? Number(form.montant_annuel) : null,
      nb_interventions_prevues: form.nb_interventions_prevues
        ? Number(form.nb_interventions_prevues)
        : null,
      delai_intervention_h: form.delai_intervention_h ? Number(form.delai_intervention_h) : null,
      equipements_couverts: equipements,
    };
    try {
      if (contrat) {
        const { prestataire_id: _omit, ...rest } = payload;
        void _omit;
        await api.patch(`/contrats/${contrat.id}`, rest);
      } else {
        await api.post('/contrats', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const q = equipSearch.trim().toLowerCase();
  const filteredEquip = equipOptions.filter(
    (eq) =>
      !q ||
      eq.code_equipement.toLowerCase().includes(q) ||
      (eq.designation ?? '').toLowerCase().includes(q),
  );
  const MAX_SHOWN = 200;
  const shownEquip = filteredEquip.slice(0, MAX_SHOWN);
  const equipTruncated = filteredEquip.length > MAX_SHOWN;

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              {contrat ? `Modifier ${contrat.reference}` : 'Nouveau contrat'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3 overflow-y-auto">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prestataire *">
                <select
                  value={form.prestataire_id}
                  onChange={(e) => set('prestataire_id', e.target.value)}
                  className={inputClass}
                  required
                  disabled={!!contrat}
                >
                  <option value="">— Sélectionner —</option>
                  {prestataires.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Référence *">
                <input
                  value={form.reference}
                  onChange={(e) => set('reference', e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
            </div>
            <Field label="Libellé *">
              <input
                value={form.libelle}
                onChange={(e) => set('libelle', e.target.value)}
                className={inputClass}
                required
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type *">
                <select
                  value={form.type_contrat}
                  onChange={(e) => set('type_contrat', e.target.value)}
                  className={inputClass}
                >
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Statut *">
                <select
                  value={form.statut}
                  onChange={(e) => set('statut', e.target.value)}
                  className={inputClass}
                >
                  <option value="brouillon">Brouillon</option>
                  <option value="actif">Actif</option>
                  <option value="expire">Expiré</option>
                  <option value="resilie">Résilié</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date début *">
                <input
                  type="date"
                  value={form.date_debut}
                  onChange={(e) => set('date_debut', e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Date fin *">
                <input
                  type="date"
                  value={form.date_fin}
                  onChange={(e) => set('date_fin', e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Montant annuel (€)">
                <input
                  type="number"
                  min="0"
                  value={form.montant_annuel}
                  onChange={(e) => set('montant_annuel', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Interventions/an">
                <input
                  type="number"
                  min="0"
                  value={form.nb_interventions_prevues}
                  onChange={(e) => set('nb_interventions_prevues', e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="SLA délai (h)">
                <input
                  type="number"
                  min="0"
                  value={form.delai_intervention_h}
                  onChange={(e) => set('delai_intervention_h', e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="Équipements couverts par le contrat">
              <p className="text-xs text-gray-500 mb-1.5">
                Détermine si une intervention est « couverte » dans le rapport prestataire. Laisser
                vide = tous les équipements sont couverts.
              </p>
              <input
                value={equipSearch}
                onChange={(e) => setEquipSearch(e.target.value)}
                placeholder="Rechercher un équipement (code ou désignation)…"
                className={inputClass}
              />
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                <span>{equipements.length} équipement(s) sélectionné(s)</span>
                {equipements.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEquipements([])}
                    className="text-primary-600 hover:underline"
                  >
                    Tout désélectionner
                  </button>
                )}
              </div>
              <div className="mt-1 max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {shownEquip.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-400">Aucun équipement.</p>
                ) : (
                  shownEquip.map((eq) => (
                    <label
                      key={eq.id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={equipements.includes(eq.id)}
                        onChange={() => toggleEquip(eq.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="font-medium text-gray-800 whitespace-nowrap">
                        {eq.code_equipement}
                      </span>
                      <span className="text-gray-500 truncate">{eq.designation}</span>
                    </label>
                  ))
                )}
              </div>
              {equipTruncated && (
                <p className="mt-1 text-xs text-gray-400">
                  {filteredEquip.length} résultats — affinez la recherche pour tous les voir.
                </p>
              )}
            </Field>

            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {contrat ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface Comparaison {
  periode: { period: string; label: string; type: string };
  contractuel: {
    nb_interventions_prevues: number | null;
    montant: number | null;
    delai_intervention_h: number | null;
  };
  realise: { nb_interventions: number; cout_total: number; satisfaction_moyenne: number | null };
  ecarts: { nb_interventions: number | null; taux_realisation: number | null; cout: number | null };
}

function ComparaisonModal({ contrat, onClose }: { contrat: ContratRow; onClose(): void }) {
  const year = new Date().getFullYear();
  const [period, setPeriod] = useState(`${year}-Q1`);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Comparaison | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: Comparaison }>(
        `/contrats/${contrat.id}/comparaison?period=${period}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const periods: string[] = [];
  for (const y of [year, year - 1]) {
    for (const q of [1, 2, 3, 4]) periods.push(`${y}-Q${q}`);
    for (let m = 1; m <= 12; m++) periods.push(`${y}-${String(m).padStart(2, '0')}`);
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Comparaison — {contrat.reference}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Période</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className={inputClass}
                >
                  {periods.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Comparer
              </button>
            </div>

            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {data && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    title="Interventions"
                    contractuel={data.contractuel.nb_interventions_prevues ?? '—'}
                    realise={data.realise.nb_interventions}
                  />
                  <StatCard
                    title="Montant / Coût (€)"
                    contractuel={data.contractuel.montant ?? '—'}
                    realise={data.realise.cout_total}
                  />
                </div>
                <div className="rounded-lg border border-gray-200 p-3 text-sm space-y-1">
                  <Row
                    label="Taux de réalisation"
                    value={
                      data.ecarts.taux_realisation != null
                        ? `${data.ecarts.taux_realisation} %`
                        : '—'
                    }
                  />
                  <Row
                    label="Écart interventions"
                    value={
                      data.ecarts.nb_interventions != null
                        ? String(data.ecarts.nb_interventions)
                        : '—'
                    }
                  />
                  <Row
                    label="Écart coût (€)"
                    value={
                      data.ecarts.cout != null
                        ? `${data.ecarts.cout > 0 ? '+' : ''}${data.ecarts.cout.toLocaleString('fr-FR')}`
                        : '—'
                    }
                  />
                  <Row
                    label="Satisfaction moyenne"
                    value={
                      data.realise.satisfaction_moyenne != null
                        ? `${data.realise.satisfaction_moyenne} / 5`
                        : '—'
                    }
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Contractuel proraté sur la période ({data.periode.label}).
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function StatCard({
  title,
  contractuel,
  realise,
}: {
  title: string;
  contractuel: number | string;
  realise: number;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <p className="text-xs font-medium text-gray-500 uppercase">{title}</p>
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-xs text-gray-500">Prévu</span>
        <span className="text-sm font-semibold text-gray-700">{contractuel}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-gray-500">Réalisé</span>
        <span className="text-lg font-bold text-primary-600">{realise}</span>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('fr-FR');
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
